import { AdgroupFormData, DeviceModel } from '@tt4b/ttam-enums';
import { NetStateEnum, useStdAdgroupBackend, useStore } from '@tt4b/ttam-hooks';
import { walkTreeBFS } from '@tt4b/ttam-utils';
import { computed, Ref } from '@vue/composition-api';
import { cloneDeep, isObject, isArray } from 'lodash-es';

export function useDataSource() {
  const { getStore } = useStore();
  const { data: deviceModelList, isLoading, run: loadData } = getStore(NetStateEnum.deviceModelList);

  return {
    deviceModelList,
    isLoading,
    loadData,
  };
}

export function usePropDeviceModelSortList(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { getStore } = useStore();
  const { data: deviceModelList } = getStore(NetStateEnum.deviceModelList);
  const { sortOptions } = useStdAdgroupBackend({ adgroupFormData: adgroupData });
  const deviceModelSortList = computed(() => sortOptions(deviceModelList?.value || []));

  return {
    deviceModelSortList,
  };
}

export function usePropDeviceModelOptions(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { deviceModelSortList } = usePropDeviceModelSortList({
    adgroupData,
  });
  /**
   * 从全量设备机型列表中 pick 出作为 option 的列表
   * is_active 为 ture
   * 只显示两级，所以不是递归，只遍历两层
   */
  const pickActiveTree = (list: DeviceModel[]) => {
    function copyTreeData(obj) {
      if (!isObject(obj)) {
        return obj;
      }
      let newObj = isArray(obj) ? [] : ({} as DeviceModel);
      for (const key of Object.keys(obj)) {
        const curVal = obj[key] as DeviceModel;
        if (!isObject(curVal)) {
          newObj[key] = obj[key];
        } else if (isArray(curVal) || curVal?.is_active) {
          newObj[key] = copyTreeData(curVal) as never;
        }
      }
      if (isArray(newObj)) {
        newObj = newObj?.filter(item => item);
      }
      return newObj;
    }
    const activeCpy = copyTreeData({ children: list });
    return (activeCpy.children || []).filter(i => i);
  };

  const platform = computed(() => adgroupData.value.platform);

  const deviceModelOptions: Ref<DeviceModel[]> = computed(() => {
    const list = cloneDeep(deviceModelSortList.value);
    const opts = pickActiveTree(list);
    if (!platform.value) {
      return opts;
    }
    return opts.filter(device => device.operation_system === platform.value);
  });

  return {
    deviceModelOptions,
  };
}

export function usePropDeviceModelName(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { deviceModelSortList } = usePropDeviceModelSortList({
    adgroupData,
  });
  /** 设备机型 Map */
  const deviceModelMap = computed(() => {
    const cpy = cloneDeep(deviceModelSortList.value);
    const map = {};
    if (cpy.length > 0) {
      walkTreeBFS({ root: { children: cpy }, handler: (node: DeviceModel) => (map[node.id] = node) });
    }
    return map;
  });
  /** 选中的设备机型数组 */
  const deviceModelArr = computed<DeviceModel[]>(() =>
    adgroupData.value.deviceModels.map(id => deviceModelMap.value[id])
  );

  /** 选中的设备机型名称数组 */
  const deviceModelName = computed(() => deviceModelArr.value?.map(item => item.name));
  return {
    deviceModelMap,
    deviceModelName,
  };
}
